import { useEffect, useMemo, useState } from "react";
import api, { formatAxiosError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCapabilities } from "@/context/CapabilitiesContext";
import { useDialog } from "@/context/DialogContext";
import {
  UserPlus, Plus, Trash, PencilSimple, CheckCircle, X, Coins, TrendUp,
  Users, Handshake, Clock, Paperclip, FileArrowUp, FileText, Briefcase,
} from "@phosphor-icons/react";

const STATUSES = ["submitted", "screening", "interviewed", "hired", "rejected"];
const REWARD_STATUSES = ["pending", "approved", "paid"];

const STATUS_COLORS = {
  submitted: "bg-blue-100 text-blue-800 border-blue-300",
  screening: "bg-amber-100 text-amber-800 border-amber-300",
  interviewed: "bg-purple-100 text-purple-800 border-purple-300",
  hired: "bg-green-100 text-green-800 border-green-400",
  rejected: "bg-gray-100 text-gray-700 border-gray-300",
};
const REWARD_COLORS = {
  pending: "bg-yellow-100 text-yellow-800 border-yellow-300",
  approved: "bg-blue-100 text-blue-800 border-blue-300",
  paid: "bg-green-100 text-green-800 border-green-400",
};

export default function Referrals() {
  const { user } = useAuth();
  const { can } = useCapabilities();
  const { confirm } = useDialog();
  const [tab, setTab] = useState(can("view_all_referrals") ? "all" : "mine");
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [config, setConfig] = useState({ default_reward_amount: 500, reward_currency: "USD" });
  const [filterStatus, setFilterStatus] = useState("");
  const [q, setQ] = useState("");
  const [dlg, setDlg] = useState(null); // {mode:"create"|"edit",item?}
  const [msg, setMsg] = useState({ type: "", text: "" });
  const [loading, setLoading] = useState(false);

  const flash = (type, text) => { setMsg({ type, text }); setTimeout(() => setMsg({ type: "", text: "" }), 4000); };

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (tab === "mine") params.set("mine", "true");
      if (filterStatus) params.set("status", filterStatus);
      if (q) params.set("q", q);
      const [rl, sm, cf] = await Promise.all([
        api.get(`/referrals?${params.toString()}`),
        api.get("/referrals/me/summary"),
        api.get("/referrals/config"),
      ]);
      setItems(rl.data.referrals || []);
      setSummary(sm.data);
      setConfig(cf.data);
    } catch (e) {
      flash("error", formatAxiosError(e));
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab, filterStatus, q]);

  const del = async (r) => {
    const ok = await confirm({
      title: `Delete referral for "${r.candidate_name}"?`,
      description: "This will remove the referral and any associated reward tracking.",
      confirmText: "Delete referral",
      variant: "destructive",
    });
    if (!ok) return;
    try { await api.delete(`/referrals/${r.id}`); await load(); flash("ok", "Deleted."); }
    catch (e) { flash("error", formatAxiosError(e)); }
  };

  return (
    <div className="p-8 lg:p-12">
      <div className="mb-8">
        <div className="eyebrow mb-2 flex items-center gap-2"><UserPlus size={12} weight="bold" /> 02 / Talent</div>
        <div className="flex flex-wrap items-center justify-between gap-4">
          <h1 className="font-display text-4xl font-black tracking-tight">Referrals</h1>
          <button onClick={() => setDlg({ mode: "create" })} className="btn-primary flex items-center gap-2" data-testid="new-referral-btn">
            <Plus size={14} weight="bold" /> Refer a candidate
          </button>
        </div>
        <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
          Refer talented people to open roles and earn rewards when they get hired.
          Default reward: <b>{config.reward_currency} {config.default_reward_amount?.toLocaleString?.() ?? config.default_reward_amount}</b> per hire.
        </p>
      </div>

      {msg.text && (
        <div className={`mb-6 px-4 py-2 text-sm border ${msg.type === "ok" ? "border-green-500 bg-green-50 text-green-700" : "border-red-500 bg-red-50 text-red-700"}`} data-testid="referral-msg">
          {msg.text}
        </div>
      )}

      {/* Summary cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <SummaryCard icon={Users} label="Total referrals" value={summary.total} />
          <SummaryCard icon={Handshake} label="Hired" value={summary.hired} accent="green" />
          <SummaryCard icon={Clock} label="In progress" value={summary.in_progress} accent="amber" />
          <SummaryCard icon={Coins} label="Earned" accent="blue"
            value={(summary.earnings_by_currency || []).map(e => `${e.currency} ${e.total.toLocaleString()}`).join(", ") || "—"}
            sub={(summary.pending_by_currency || []).length > 0 ? `Pending: ${summary.pending_by_currency.map(e => `${e.currency} ${e.total.toLocaleString()}`).join(", ")}` : null} />
        </div>
      )}

      {/* Filters + tabs */}
      <div className="flex flex-wrap items-center gap-3 mb-6">
        {can("view_all_referrals") && (
          <div className="inline-flex border border-border">
            <button onClick={() => setTab("all")} className={`px-3 py-1.5 text-xs font-semibold ${tab === "all" ? "bg-[#002FA7] text-white" : "bg-white"}`} data-testid="tab-all">All</button>
            <button onClick={() => setTab("mine")} className={`px-3 py-1.5 text-xs font-semibold border-l border-border ${tab === "mine" ? "bg-[#002FA7] text-white" : "bg-white"}`} data-testid="tab-mine">Mine</button>
          </div>
        )}
        <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="border border-border px-3 py-1.5 text-sm bg-white" data-testid="filter-status">
          <option value="">All statuses</option>
          {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name / email / role" className="border border-border px-3 py-1.5 text-sm bg-white min-w-[280px]" data-testid="search-referrals" />
      </div>

      {/* Table */}
      <div className="border border-border overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted border-b border-border">
            <tr>
              <th className="text-left px-3 py-2 font-semibold">Candidate</th>
              <th className="text-left px-3 py-2 font-semibold">Role</th>
              {tab !== "mine" && <th className="text-left px-3 py-2 font-semibold">Referred by</th>}
              <th className="text-left px-3 py-2 font-semibold">Status</th>
              <th className="text-left px-3 py-2 font-semibold">Reward</th>
              <th className="text-left px-3 py-2 font-semibold">Date</th>
              <th className="text-left px-3 py-2 font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? <tr><td colSpan={7} className="px-3 py-6 text-center text-muted-foreground">Loading…</td></tr>
              : items.length === 0 ? <tr><td colSpan={7} className="px-3 py-8 text-center text-muted-foreground">No referrals yet. Click <b>Refer a candidate</b> to get started.</td></tr>
              : items.map(r => (
                <tr key={r.id} className="border-b border-border hover:bg-muted/40" data-testid={`referral-row-${r.id}`}>
                  <td className="px-3 py-2">
                    <div className="font-semibold">{r.candidate_name}</div>
                    <div className="text-xs text-muted-foreground">{r.candidate_email}</div>
                  </td>
                  <td className="px-3 py-2">{r.position}</td>
                  {tab !== "mine" && <td className="px-3 py-2">{r.referrer?.name || "—"}</td>}
                  <td className="px-3 py-2">
                    <span className={`px-2 py-0.5 text-[10px] font-semibold uppercase border ${STATUS_COLORS[r.status] || ""}`}>{r.status}</span>
                  </td>
                  <td className="px-3 py-2">
                    <div>{r.reward_currency} {(r.reward_amount || 0).toLocaleString()}</div>
                    <span className={`inline-block mt-0.5 px-2 py-0.5 text-[10px] font-semibold uppercase border ${REWARD_COLORS[r.reward_status] || ""}`}>{r.reward_status}</span>
                  </td>
                  <td className="px-3 py-2 text-xs text-muted-foreground">{new Date(r.created_at).toLocaleDateString()}</td>
                  <td className="px-3 py-2">
                    <div className="flex gap-1">
                      <button onClick={() => setDlg({ mode: "edit", item: r })} className="p-1 hover:bg-muted" title="Edit" data-testid={`edit-${r.id}`}><PencilSimple size={14} weight="bold" /></button>
                      {(r.referrer_id === user.id || can("manage_referrals")) && (
                        <button onClick={() => del(r)} className="p-1 hover:bg-muted text-red-600" title="Delete" data-testid={`del-${r.id}`}><Trash size={14} weight="bold" /></button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            }
          </tbody>
        </table>
      </div>

      {dlg && <ReferralDialog dlg={dlg} onClose={() => setDlg(null)} onSaved={async () => { setDlg(null); await load(); flash("ok", "Saved."); }} can={can} />}
    </div>
  );
}

function SummaryCard({ icon: Icon, label, value, sub, accent = "blue" }) {
  const accentMap = {
    blue: "text-[#002FA7]", green: "text-green-600", amber: "text-amber-600", purple: "text-purple-600",
  };
  return (
    <div className="border border-border p-4">
      <div className="flex items-center gap-2 mb-2">
        <Icon size={16} weight="bold" className={accentMap[accent] || ""} />
        <div className="text-xs font-mono-mini uppercase tracking-widest text-muted-foreground">{label}</div>
      </div>
      <div className="font-display text-2xl font-black">{value ?? "—"}</div>
      {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
    </div>
  );
}

function ReferralDialog({ dlg, onClose, onSaved, can }) {
  const editing = dlg.mode === "edit" ? dlg.item : null;
  const [form, setForm] = useState({
    candidate_name: editing?.candidate_name || "",
    candidate_email: editing?.candidate_email || "",
    candidate_phone: editing?.candidate_phone || "",
    position: editing?.position || "",
    job_id: editing?.job_id || "",
    resume_file_id: editing?.resume_file_id || "",
    attachments: editing?.attachments || [],
    notes: editing?.notes || "",
    status: editing?.status || "submitted",
    reward_amount: editing?.reward_amount ?? "",
    reward_status: editing?.reward_status || "pending",
  });
  const [resumeMeta, setResumeMeta] = useState(editing?.resume_file || null);
  const [attachmentMetas, setAttachmentMetas] = useState(editing?.attachment_files || []);
  const [openJobs, setOpenJobs] = useState([]);
  const [uploadingResume, setUploadingResume] = useState(false);
  const [uploadingAtt, setUploadingAtt] = useState(false);
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);

  const canMgr = can("manage_referrals");
  const canApprove = can("approve_referral_rewards");

  useEffect(() => { (async () => {
    try {
      const r = await api.get("/jobs?status=open");
      setOpenJobs(r.data.jobs || []);
    } catch { /* silent */ }
  })(); }, []);

  const pickJob = (jid) => {
    const j = openJobs.find(x => x.id === jid);
    if (!j) { setForm({ ...form, job_id: "" }); return; }
    setForm({ ...form, job_id: j.id, position: form.position || j.title });
  };

  const uploadFile = async (file, setBusy, cb) => {
    setBusy(true); setErr("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/uploads/file", fd, { headers: { "Content-Type": "multipart/form-data" } });
      cb(r.data);
    } catch (e) { setErr(formatAxiosError(e)); }
    finally { setBusy(false); }
  };

  const onResumeChange = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    uploadFile(file, setUploadingResume, (d) => {
      setForm(f => ({ ...f, resume_file_id: d.file_id || d.id }));
      setResumeMeta({ file_id: d.file_id || d.id, filename: d.filename || file.name, size_bytes: d.size_bytes || file.size, content_type: d.content_type || file.type });
    });
  };
  const onAttachmentChange = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    uploadFile(file, setUploadingAtt, (d) => {
      const fid = d.file_id || d.id;
      setForm(f => ({ ...f, attachments: [...f.attachments, fid] }));
      setAttachmentMetas(prev => [...prev, { file_id: fid, filename: d.filename || file.name, size_bytes: d.size_bytes || file.size, content_type: d.content_type || file.type }]);
    });
    e.target.value = "";
  };
  const removeAttachment = (fid) => {
    setForm(f => ({ ...f, attachments: f.attachments.filter(a => a !== fid) }));
    setAttachmentMetas(prev => prev.filter(m => m.file_id !== fid));
  };
  const removeResume = () => {
    setForm(f => ({ ...f, resume_file_id: "" }));
    setResumeMeta(null);
  };

  const humanSize = (b) => {
    if (b == null) return "";
    const u = ["B", "KB", "MB"]; let i = 0, n = b;
    while (n >= 1024 && i < u.length - 1) { n /= 1024; i++; }
    return `${n.toFixed(n >= 10 || i === 0 ? 0 : 1)} ${u[i]}`;
  };

  const save = async () => {
    setErr(""); setSaving(true);
    try {
      if (editing) {
        const payload = {};
        ["candidate_name", "candidate_email", "candidate_phone", "position", "notes", "job_id", "resume_file_id"].forEach(k => { if (form[k] !== undefined) payload[k] = form[k]; });
        payload.attachments = form.attachments;
        if (canMgr) {
          payload.status = form.status;
          if (form.reward_amount !== "") payload.reward_amount = Number(form.reward_amount);
        }
        if (canApprove) payload.reward_status = form.reward_status;
        await api.patch(`/referrals/${editing.id}`, payload);
      } else {
        await api.post("/referrals", {
          candidate_name: form.candidate_name.trim(),
          candidate_email: form.candidate_email.trim(),
          candidate_phone: form.candidate_phone.trim(),
          position: form.position.trim(),
          job_id: form.job_id || "",
          resume_file_id: form.resume_file_id || "",
          attachments: form.attachments,
          notes: form.notes.trim(),
        });
      }
      await onSaved();
    } catch (e) {
      setErr(formatAxiosError(e));
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white border border-border max-w-xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-border flex items-center justify-between">
          <h3 className="font-display text-xl font-black">{editing ? "Edit referral" : "Refer a candidate"}</h3>
          <button onClick={onClose} className="p-1 hover:bg-muted"><X size={16} weight="bold" /></button>
        </div>
        <div className="p-6 space-y-3">
          {err && <div className="text-xs text-red-700 border border-red-500 bg-red-50 px-3 py-2">{err}</div>}
          <Field label="Refer to an open position (optional)">
            <select className="input" value={form.job_id} onChange={(e) => pickJob(e.target.value)} data-testid="ref-job-select">
              <option value="">— Not linked to a posting —</option>
              {openJobs.map(j => (
                <option key={j.id} value={j.id}>
                  {j.title}{j.department ? ` · ${j.department}` : ""}{j.location ? ` · ${j.location}` : ""}
                </option>
              ))}
            </select>
            {form.job_id && (() => {
              const j = openJobs.find(x => x.id === form.job_id) || editing?.job;
              return j ? (
                <div className="mt-2 text-xs border border-blue-200 bg-blue-50 px-2 py-1.5 flex items-center gap-2">
                  <Briefcase size={12} weight="bold" className="text-[#002FA7]" />
                  <span>Linked to <b>{j.title}</b></span>
                  <span className="ml-auto px-1.5 py-0.5 text-[10px] font-semibold uppercase border border-green-400 bg-green-100 text-green-800">{j.status || "open"}</span>
                </div>
              ) : null;
            })()}
          </Field>
          <Field label="Candidate name*"><input className="input" value={form.candidate_name} onChange={(e) => setForm({ ...form, candidate_name: e.target.value })} data-testid="ref-name" /></Field>
          <Field label="Email*"><input className="input" type="email" value={form.candidate_email} onChange={(e) => setForm({ ...form, candidate_email: e.target.value })} data-testid="ref-email" /></Field>
          <Field label="Phone"><input className="input" value={form.candidate_phone} onChange={(e) => setForm({ ...form, candidate_phone: e.target.value })} data-testid="ref-phone" /></Field>
          <Field label={form.job_id ? "Position (auto-filled from posting)" : "Position / role*"}>
            <input className="input" value={form.position} onChange={(e) => setForm({ ...form, position: e.target.value })} data-testid="ref-position" />
          </Field>

          {/* Resume upload */}
          <Field label="Resume">
            {resumeMeta ? (
              <div className="flex items-center gap-2 border border-border px-3 py-2 bg-muted/30 text-sm">
                <FileText size={14} weight="bold" className="text-[#002FA7]" />
                <span className="flex-1 truncate">{resumeMeta.filename}</span>
                <span className="text-xs text-muted-foreground">{humanSize(resumeMeta.size_bytes)}</span>
                <button onClick={removeResume} className="text-red-600 text-xs" data-testid="ref-remove-resume">Remove</button>
              </div>
            ) : (
              <label className="flex items-center gap-2 border border-dashed border-border px-3 py-3 hover:bg-muted/30 cursor-pointer text-sm">
                <FileArrowUp size={16} weight="bold" className="text-[#002FA7]" />
                <span>{uploadingResume ? "Uploading resume…" : "Upload resume (PDF, DOC, DOCX…)"}</span>
                <input type="file" className="hidden" onChange={onResumeChange} accept=".pdf,.doc,.docx,.rtf,.txt" data-testid="ref-resume-input" />
              </label>
            )}
          </Field>

          {/* Additional documents */}
          <Field label="Other documents (portfolio, cover letter, references…)">
            {attachmentMetas.length > 0 && (
              <ul className="space-y-1 mb-2">
                {attachmentMetas.map(m => (
                  <li key={m.file_id} className="flex items-center gap-2 border border-border px-3 py-1.5 bg-muted/30 text-xs">
                    <Paperclip size={12} weight="bold" />
                    <span className="flex-1 truncate">{m.filename}</span>
                    <span className="text-muted-foreground">{humanSize(m.size_bytes)}</span>
                    <button onClick={() => removeAttachment(m.file_id)} className="text-red-600" data-testid={`ref-remove-att-${m.file_id}`}>Remove</button>
                  </li>
                ))}
              </ul>
            )}
            <label className="flex items-center gap-2 border border-dashed border-border px-3 py-2 hover:bg-muted/30 cursor-pointer text-sm">
              <Paperclip size={14} weight="bold" />
              <span>{uploadingAtt ? "Uploading…" : "Add another document"}</span>
              <input type="file" className="hidden" onChange={onAttachmentChange} data-testid="ref-att-input" />
            </label>
          </Field>

          <Field label="Notes"><textarea className="input min-h-[80px]" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="ref-notes" /></Field>
          {editing && canMgr && (
            <>
              <Field label="Status">
                <select className="input" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} data-testid="ref-status">
                  {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </Field>
              <Field label={`Reward amount (${editing.reward_currency})`}>
                <input className="input" type="number" min="0" step="any" value={form.reward_amount} onChange={(e) => setForm({ ...form, reward_amount: e.target.value })} data-testid="ref-reward-amount" />
              </Field>
            </>
          )}
          {editing && canApprove && (
            <Field label="Reward status">
              <select className="input" value={form.reward_status} onChange={(e) => setForm({ ...form, reward_status: e.target.value })} data-testid="ref-reward-status">
                {REWARD_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </Field>
          )}
        </div>
        <div className="px-6 py-4 border-t border-border flex items-center justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm border border-border hover:bg-muted">Cancel</button>
          <button onClick={save} disabled={saving} className="btn-primary flex items-center gap-2" data-testid="save-referral">
            <CheckCircle size={14} weight="bold" /> {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="block">
      <span className="block text-xs font-mono-mini uppercase tracking-widest text-muted-foreground mb-1">{label}</span>
      {children}
    </div>
  );
}
